import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { MUTATIONS, useMutation } from "../../../api";
import { rules } from "../../../lib/rules.jsx";
import cx from "../../../lib/cx.js";
import { Spinner } from "../../../design-system/utils/Spinner/Spinner.jsx";
import { uploadFile } from "../../../lib/uploader.jsx";
import { uploadVideo } from "../../../lib/videoUploadHandler.jsx";
import ProgressIndicator from "../../../design-system/components/ProgressIndicator/ProgressIndicator.jsx";
import Button from "../../../design-system/components/Button/Button.jsx";
import InputSubmit from "../../../design-system/components/InputSubmit/InputSubmit.jsx";
import { Variants } from "../../../design-system/components/Button/Variants.jsx";
import Layout from "../../../design-system/components/Layout/Layout.jsx";
import { triggerConfetti } from "../../../design-system/utils/Celebration/confetti.jsx";
import Header from "../../../design-system/components/Header/Header.jsx";
import { useForm, useFormDispatch } from "./FormContext.jsx";
import Steps from "./Steps.jsx";
import styles from "./showroomForm.css";

/**
 * Converts a given rule object into a corresponding form validation rule.
 *
 * @function
 * @param {Object} rule - The rule object containing the validation rule type and its value.
 * @param {string} rule.type - The type of validation rule (e.g., "required", "minLength", "maxLength").
 * @param {*} rule.value - The value associated with the rule type, used for specific validation types (e.g., length for minLength/maxLength).
 * @returns {(Function|null)} The corresponding validation rule from the `rules` object or null if the rule type is not recognized.
 */
const ruleToFormRule = ({ type, value }) => {
  switch (type) {
    case "required":
      return rules.isRequired;
    case "minLength":
      return rules.minLength(value);
    case "maxLength":
      return rules.maxLength(value);
    default:
      return null;
  }
};

/**
 * Generates validation rules for a given form definition.
 *
 * @param {Object} formDefinition - The definition of the form containing fields and other specifications.
 * @param {Array} formDefinition.fields - List of field objects in the form.
 * @param {string} formDefinition.fields[].key - Unique identifier for the form field.
 * @param {string} formDefinition.fields[].type - The type of the form field (e.g., "projectUpload", "image").
 * @param {Array} [formDefinition.fields[].rules] - Optional array of custom rules defined for the field.
 * @returns {Object} An object where keys are field keys and values are arrays of validation rules applicable to those fields.
 */
const getFormRules = (formDefinition) => {
  const { fields } = formDefinition;
  const formRules = {};
  for (const { key, type, rules: fieldRules } of fields) {
    formRules[key] = fieldRules
      ? fieldRules.map(ruleToFormRule).filter(Boolean)
      : [];
    if (type === "projectUpload") {
      formRules[key].push(rules.isUrl);
      formRules[key].push(rules.isMax25MB);
      formRules[key].push(rules.isImageOrAudioOrApplicationOrTextOrVideo);
    }
    if (type === "image") {
      formRules[key].push(rules.isMax25MB);
      formRules[key].push(rules.isImage);
    }
  }
  return formRules;
};

const defaultValuesMap = {
  selection: null,
  image: null,
  projectUpload: null,
  tags: [],
};
/**
 * Returns the default form value based on the provided type.
 *
 * @param {string} type - The type of the form field for which the default value is to be retrieved.
 *                        Supported types include "selection", "tags", "image", "projectUpload", and others.
 * @returns {*} The default value for the specified form type. Returns null for "selection", "image",
 *              and "projectUpload", an empty array for "tags", and an empty string for unsupported types.
 */
const getDefaultFormValue = (type) => defaultValuesMap[type] ?? "";

/**
 * Generates and returns the initial values for a form based on an entry and project form definition.
 *
 * @param {Object} params - The input parameters.
 * @param {Object} params.entry - The existing form entry data, if any.
 // * @param {Object} params.entry.formData - The stored data for each form field.
 * @param {Object} params.projectFormDefinition - The project form definition object.
 // * @param {Array} params.projectFormDefinition.fields - The list of fields in the form definition.
 // * @param {string} params.projectFormDefinition.fields[].key - The unique key for a specific form field.
 // * @param {string} params.projectFormDefinition.fields[].type - The data type of a specific form field.
 *
 * @returns {Object} The generated initial values for the form, including default values when no entry data exists.
 */
const getInitVals = ({ entry, projectFormDefinition }) => {
  const { fields } = projectFormDefinition;
  const data = {};
  for (const { key, type } of fields) {
    if (entry && entry.formData && entry.formData[key]) {
      data[key] = entry.formData[key];
    } else {
      data[key] = getDefaultFormValue(type);
    }
  }
  return data;
};

/**
 * Checks whether a given field is not empty.
 * This applies to all fields in general, independent of specific values.
 * This is to make sure that even if a field is not required, we still do not store
 * empty values.
 *
 * @param {string|Object} value - The value to validate, which can be a string or an object.
 * @returns {boolean} - Returns true if the field is not empty, otherwise false if it is empty.
 */
const fieldNotEmpty = (value) => {
  // The value can either be a string, if so the length should be > 0
  // Or it can be an object, in this case it can be a labelled field,
  // where data contains the string or a file with a size.
  // Or it can be an object but an unlabelled field,
  // in which case the value directly contains the file with a size.
  return (
    value &&
    ((typeof value === "string" && value.length > 0) ||
      (value.data &&
        (value.data.length > 0 || value.data.size || value.type === "video")) ||
      value.size)
  );
};

/**
 * Represents a functional component for a showroom form, with features such as field validation,
 * error handling, and asynchronous submission.
 *
 * This component handles dynamic field rendering, input validation, state management, and submission logic
 * based on the provided `projectFormDefinition`. It supports working with various field types, including file
 * and video uploads, and provides a proper mechanism to update errors and manage submission states.
 *
 * @param {Object} props - The component's input properties.
 * @param {Object} props.entry - The initial entry state loaded into the form.
 * @param {Object} props.smallTeam - Information regarding the associated team.
 * @param {Object} props.projectFormDefinition - Configuration of the form, including fields and validation rules.
 */
export const ShowroomForm = ({ entry, smallTeam, projectFormDefinition }) => {
  const { fields } = projectFormDefinition;
  const [doSetEntry] = useMutation(MUTATIONS.setShowroomEntry);
  const [doCreateToken] = useMutation(MUTATIONS.createPeertubeToken);
  const initVals = getInitVals({ entry, projectFormDefinition });
  const [edit] = useState(!!entry);
  const contextFormData = useForm();
  const [formData, setFormData] = useState(edit ? initVals : contextFormData);
  const [errors, setErrors] = useState({});
  const formRules = getFormRules(projectFormDefinition);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [isSubmissionPending, setIsSubmissionPending] = useState(false);
  const [completedSuccessfully, setCompletedSuccessfully] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const completedSuccessfullyMessageTimeout = useRef(null);
  const videoUploadTimeoutRef = useRef(null);
  const dispatch = useFormDispatch();
  const navigate = useNavigate();

  const isRequired = useCallback(
    (fieldKey) => {
      const fieldRules = fields.find((field) => field.key === fieldKey)?.rules;
      if (fieldRules && fieldRules.length > 0) {
        return fieldRules.some((rule) => rule.type === "required");
      }
      return false;
    },
    [fields],
  );

  useEffect(() => {
    if (!edit && contextFormData) {
      setFormData(contextFormData);
    }
  }, [contextFormData, edit]);

  useEffect(() => {
    // Only enable the submit button if no important errors (errors for required fields) are present
    let submitShouldBeDisabled = isSubmissionPending;
    if (!submitShouldBeDisabled) {
      for (const field of fields) {
        if (isRequired(field.key)) {
          // Here we check not only for errors, but also check again for whether a value for the field is present
          // this is needed due to the complexity of the project upload field, because data could exist but be empty
          // for some reason.
          // Ideally of course, required but empty fields would have an error stored in the error array.
          if (errors[field.key] || !fieldNotEmpty(formData[field.key])) {
            submitShouldBeDisabled = true;
            break;
          }
        }
      }
    }

    setIsSubmitDisabled(submitShouldBeDisabled);
  }, [errors, formData, isSubmissionPending, fields, isRequired]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (completedSuccessfullyMessageTimeout.current) {
        clearTimeout(completedSuccessfullyMessageTimeout.current);
      }
      if (videoUploadTimeoutRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        clearTimeout(videoUploadTimeoutRef.current);
      }
    };
  }, []);

  /**
   * A function that validates the value of a specific form field when an event is triggered.
   *
   * @param {Event} e - The event object triggered by the field interaction.
   * @property {Object} e.target - The target element where the event occurred.
   * @property {string} e.target.name - The name attribute of the target element, indicating the field name.
   * @property {string} e.target.value - The value attribute of the target element, indicating the field's value.
   */
  const validate = (e) => {
    if (e?.target?.name) validateFieldValue(e.target.name, e.target.value);
  };

  /**
   * Function to validate form data fields based on predefined rules and manage error states.
   *
   * The function clears any prior errors for the specified field and evaluates
   * the field's value against a set of validation rules. If a validation rule is
   * violated, the function updates the error state with the appropriate error message.
   *
   * @param {string} fieldKey - The unique key identifying the field to validate.
   * @param {*} value - The value of the field to validate.
   * @return {boolean} - Whether the field is valid.
   */
  const validateFieldValue = (fieldKey, value) => {
    // Clear prior errors
    const updatedErrors = { ...errors };
    if (Object.hasOwn(updatedErrors, fieldKey)) delete updatedErrors[fieldKey];
    // Validate
    const fieldRules = formRules[fieldKey];
    if (fieldRules) {
      for (const fieldRule of fieldRules) {
        const valid = fieldRule[0](value); // Test the rule
        if (!valid) {
          // Set the error - sometimes we need info from the value to construct the error string.
          if (typeof fieldRule[1] === "string") {
            updatedErrors[fieldKey] = fieldRule[1];
          } else if (typeof fieldRule[1] === "function") {
            updatedErrors[fieldKey] = fieldRule[1](value);
          }
          // If one rule is not met we don't need to check the others - we know the field is invalid.
          break;
        }
      }
    }
    // Update errors
    setErrors({
      ...updatedErrors,
    });
  };

  /**
   * Asynchronously handles the form submission process. This function performs multiple tasks, including:
   * - Validating the form data and ensuring no errors exist before proceeding.
   * - Uploading file fields (if present) and properly handling the upload result or any errors encountered during the upload.
   * - Constructing a payload object that includes necessary information such as team ID, form data, and publication status.
   * - Setting the finalized entry data to an external state handler.
   * - Managing the submission state to indicate whether the submission process is pending.
   *
   * The function uploads only newly selected file fields and excludes empty fields from submission. Error handling is performed at both the field and submission levels.
   */
  const submitForm = async (e) => {
    e.preventDefault();

    if (isSubmitDisabled) return;
    setIsSubmissionPending(true);

    // construct formData
    const submittableFormData = {};
    const uploadErrors = {};

    await Promise.all(
      fields.map(async (field) => {
        // Do not keep empty elements and also add a final validation check && valueAdheresToRules(formData[field.key], rules[field.key])
        if (fieldNotEmpty(formData[field.key]) && !uploadErrors[field.key]) {
          // If the field is a file, upload it and store the result
          // This only catches newly selected files, as otherwise the file field has just the
          // deserialized file info retrieved from the backend as a value.
          // So, only new files are uploaded.
          if (
            formData[field.key] instanceof File &&
            (field.type === "projectUpload" || field.type === "image")
          ) {
            const onError = (error) => {
              setErrors({
                ...uploadErrors,
                [field.key]: `Dein Projekt wurde gespeichert, aber mit dieser Datei ist etwas schiefgelaufen: ${error.message}`,
              });
              uploadErrors[field.key] = error.message;
            };

            const onProgress = (progress) => {
              setVideoUploadProgress(progress);
            };

            if (formData[field.key].type.startsWith("video")) {
              const onUploaded = (result) => {
                // Add the info about the uploaded video to the payload.

                if (result.video) {
                  submittableFormData[field.key] = {
                    data: result.video,
                    type: "video",
                  };
                  setVideoUploadProgress(0);
                }
              };

              const { token, channelId } = await doCreateToken(); // Get Peertube token to use for uploading

              // Upload video to peertube
              await uploadVideo({
                file: formData[field.key],
                key: field.key,
                token: token,
                channelId: channelId,
                onProgress,
                onUploaded,
                onError,
                timeoutRef: videoUploadTimeoutRef,
              });
            } else {
              const onUploaded = (result) => {
                if (Object.hasOwn(result, "file")) {
                  // We also receive the file from the upload handler,
                  // but we do not need it.
                  delete result.file;
                }
                // Add the info about the uploaded file to the payload.
                submittableFormData[field.key] = { data: result, type: "file" };
              };

              // Upload file to the server
              await uploadFile({
                key: field.key,
                category: "showroom-uploads",
                file: formData[field.key],
                onUploaded,
                onError,
              });
            }
          } else {
            if (
              (field.type === "selection" || field.type === "tags") &&
              Object.hasOwn(formData[field.key], "data")
            ) {
              // Due to labeling the formData in the backend, we obtain the labelled formData here.
              // To store the data correctly (legacy compatibility) we need to store only the value.
              submittableFormData[field.key] = formData[field.key].data;
            } else {
              submittableFormData[field.key] = formData[field.key];
            }
          }
        }
      }),
    );

    const newEntry = {
      smallTeamId: smallTeam.id,
      formData: submittableFormData,
      workshopId: smallTeam.workshopId,
    };

    try {
      await doSetEntry(newEntry);
      setIsSubmissionPending(false);
      if (Object.keys(uploadErrors).length === 0) {
        setCompletedSuccessfully(true);
        triggerConfetti();

        completedSuccessfullyMessageTimeout.current = setTimeout(() => {
          setCompletedSuccessfully(false);
          navigate("..");
        }, 2000);
      } else {
        // There was an issue with a file upload, but the showroom entry still saved
        // (because the file was not mandatory)
        setIsSubmissionPending(false);
        if (Object.keys(uploadErrors).length) {
          const firstErrorField = Object.keys(uploadErrors)[0];
          const errorText = uploadErrors[firstErrorField];
          const newErrorText = `Dein Projekt wurde gespeichert aber hier gibt es noch ein Problem: ${errorText}`;
          setErrors({ ...uploadErrors, [firstErrorField]: newErrorText });
          const firstErrorStepIndex = fields.findIndex(
            (field) => field.key === firstErrorField,
          );
          dispatch({ type: "setStep", step: firstErrorStepIndex });
        }
      }
    } catch {
      setIsSubmissionPending(false);
      if (Object.keys(uploadErrors).length) {
        const firstErrorField = Object.keys(uploadErrors)[0];
        const firstErrorStepIndex = fields.findIndex(
          (field) => field.key === firstErrorField,
        );
        dispatch({ type: "setStep", step: firstErrorStepIndex });
      }
    }
  };

  /**
   * Handles the change event for a checkbox input element.
   *
   * Updates the form data state when a checkbox is checked or unchecked.
   * The function uses the event object to identify the name and value
   * of the checkbox and updates the corresponding key in the form data
   * with the checked state of the checkbox.
   *
   * No validation for checkbox fields.
   *
   * @param {Object} e - The event object generated by the change event.
   * @param {Object} e.target - The target element of the event.
   * @param {string} e.target.name - The name attribute of the checkbox input.
   * @param {boolean} e.target.checked - The checked state of the checkbox input.
   */
  const handleCheckboxChange = (e) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [e.target.name]: e.target.checked,
    }));
  };

  async function handleBack(e) {
    e.preventDefault();
    if (formData.step === 1) {
      navigate("..");
      return;
    }
    dispatch({ type: "prevStep" });
  }

  function handleDisabled(step) {
    // Handle the final submission step (finish screen)
    if (step === fields.length + 1) {
      return isSubmitDisabled;
    }

    // Convert step to field index (0-based)
    const fieldIndex = step - 1;

    // Validate field index bounds
    if (fieldIndex < 0 || fieldIndex >= fields.length) {
      return false;
    }

    const field = fields[fieldIndex];
    const fieldKey = field.key;

    // Check if field has validation errors
    const hasError = Boolean(errors[fieldKey]);
    if (hasError) {
      return true;
    }

    // Check if field is required, if not we can just skip it
    if (!isRequired(fieldKey)) {
      return false;
    }
    // Check if field has a value
    const fieldValue = formData[fieldKey];

    return !fieldValue;
  }

  async function handleNext(e) {
    if (formData.step === fields.length + 1) {
      await submitForm(e);
      return;
    }
    e.preventDefault();
    dispatch({ type: "nextStep" });
  }

  /**
   * Generates a subHeadline for the Header based on the current step
   *
   * @param {number} step - The current step in the form
   * @returns {string|null} - The subHeadline text or null if in edit mode
   */
  //ToDo: Headline Property in Content Management hinzufügen oder mitangeben
  const getSubHeadline = (step) => {
    if (step > fields.length) {
      return "Überprüfe deine Eingaben";
    }

    const currentField = fields[step - 1];

    return currentField?.label || null;
  };

  if (!formData) return <Spinner />;

  return (
    <Layout
      Header={() => (
        <Header
          back
          swapHeadlines={!edit}
          title={edit ? "Editieren" : "Hochladen"}
          subtitle={!edit && getSubHeadline(formData.step)}
          isDarkMode={true}
          hasBorder
        >
          {edit && (
            <Button
              variant={Variants.PRIMARY}
              width="fit"
              onClick={submitForm}
              disabled={isSubmitDisabled}
            >
              {!isSubmissionPending
                ? "Speichern"
                : `Wird hochgeladen... (${videoUploadProgress}%)`}
            </Button>
          )}
        </Header>
      )}
      centerVertically={!edit}
      Footer={
        !edit
          ? () => (
              <div className={styles.footerContainer}>
                <Button
                  width={"fit-content"}
                  onClick={handleBack}
                  variant={Variants.PRIMARY}
                >
                  Zurück
                </Button>
                <ProgressIndicator
                  countItems={fields.length + 1}
                  step={formData.step}
                />
                <div className={styles.submitButtonContainer}>
                  <InputSubmit
                    form={"showroomForm"}
                    value={
                      formData.step === fields.length + 1
                        ? !isSubmissionPending
                          ? "Hochladen"
                          : `Wird hochgeladen... (${videoUploadProgress}%)`
                        : "Weiter"
                    }
                    disabled={handleDisabled(formData?.step)}
                    completedSuccessfully={completedSuccessfully}
                  />
                </div>
              </div>
            )
          : null
      }
    >
      <form
        id={"showroomForm"}
        onSubmit={handleNext}
        className={cx(
          styles.formContainer({ edit }),
          isSubmissionPending && styles.disabled,
        )}
      >
        <Steps
          validateFieldValue={validateFieldValue}
          errors={errors}
          setErrors={setErrors}
          fields={fields}
          isRequired={isRequired}
          formData={formData}
          setFormData={setFormData}
          edit={edit}
          validate={validate}
          handleCheckboxChange={handleCheckboxChange}
          isSubmissionPending={isSubmissionPending}
          isSubmitDisabled={isSubmitDisabled}
        />
      </form>
    </Layout>
  );
};
